import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

// SECTION HERO IMAGES
import heroBoiler from "../assets/images/boiler.jpeg";
import heroBathroom from "../assets/images/bathroom.jpeg";
import heroFix from "../assets/images/fix.jpeg";

// GALLERY IMAGES
import img1 from "../assets/images/image1.jpeg";
import img2 from "../assets/images/image2.jpeg";
import img3 from "../assets/images/image3.jpeg";
import img4 from "../assets/images/image4.jpeg";
import img5 from "../assets/images/image5.jpeg";
import img6 from "../assets/images/image6.jpeg";
import img7 from "../assets/images/image7.jpeg";
import img8 from "../assets/images/image8.jpeg";
import img9 from "../assets/images/image9.jpeg";
import img10 from "../assets/images/image10.jpeg";
import img11 from "../assets/images/image11.jpeg";

const GALLERY = [
  { src: img1, alt: "Boiler install – neat pipework" },
  { src: img2, alt: "Cylinder install – tidy layout" },
  { src: img3, alt: "Bathroom refurb – finished tiling" },
  { src: img4, alt: "First fix – copper runs" },
  { src: img5, alt: "Radiators and valves – aligned" },
  { src: img6, alt: "ASHP outdoor unit – clean siting" },
  { src: img7, alt: "Second fix – sanitaryware" },
  { src: img8, alt: "Boiler servicing" },
  { src: img9, alt: "Pipework detail – close up" },
  { src: img10, alt: "Shower room – final clean" },
  { src: img11, alt: "Kitchen plumbing – under-sink" },
];

function SEO() {
  const title = "Services | Beaumonts Plumbing & Heating, Lincoln";
  const description =
    "Boilers, cylinders, air source heat pumps. Bathrooms. First fix and second fix for new build and refurb. See our work and how we deliver.";
  const href = typeof window !== "undefined" ? window.location.href : "";
  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={href} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
    </>
  );
}

export default function Services() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const startX = useRef<number | null>(null);
  const location = useLocation();

  // Smooth-scroll to hash on mount/navigation (when coming from Home "Learn more")
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location]);

  // Lightbox: keyboard + body scroll lock
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (lightboxIndex === null) return;
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowRight") setLightboxIndex((i) => (i === null ? i : (i + 1) % GALLERY.length));
      if (e.key === "ArrowLeft") setLightboxIndex((i) => (i === null ? i : (i - 1 + GALLERY.length) % GALLERY.length));
    }
    window.addEventListener("keydown", onKey);
    // lock body scroll
    if (lightboxIndex !== null) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
        window.removeEventListener("keydown", onKey);
      };
    }
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex]);

  // Touch swipe for lightbox
  const onTouchStart = (e: React.TouchEvent) => (startX.current = e.touches[0].clientX);
  const onTouchEnd = (e: React.TouchEvent) => {
    if (startX.current == null || lightboxIndex === null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx > 40) setLightboxIndex((i) => (i === null ? i : (i - 1 + GALLERY.length) % GALLERY.length));
    if (dx < -40) setLightboxIndex((i) => (i === null ? i : (i + 1) % GALLERY.length));
    startX.current = null;
  };

  return (
    <>
      <SEO />

      {/* Page hero */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14 md:py-20">
        <div className="max-w-3xl">
          <p className="text-xs font-medium tracking-widest uppercase text-[#3bb3ef]">Our services</p>
          <h1 className="mt-2 text-3xl md:text-5xl font-semibold tracking-[-0.02em]">
            Local, reliable plumbing in Lincolnshire
          </h1>
          <p className="mt-3 text-[15px] md:text-base text-neutral-700">
            We survey, explain the options, price it clearly, then turn up and do the job neatly.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/contact" className="inline-flex items-center rounded-md bg-[#3bb3ef] text-white px-5 py-3 text-sm font-medium hover:brightness-110">
              Get a quote
            </Link>
            <a href="tel:+441522692592" className="inline-flex items-center rounded-md border border-neutral-300 px-5 py-3 text-sm font-medium hover:bg-neutral-50">
              Call 01522 692592
            </a>
          </div>
        </div>
      </section>

{/* ───────────────── Boilers / Cylinders / ASHP ───────────────── */}
<section
  id="boilers"
  aria-labelledby="boilers-title"
  className="relative scroll-mt-24 md:scroll-mt-28 border-t border-neutral-200 bg-white"
>
  {/* soft backdrop for depth */}
  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_40%_at_20%_0%,#f6f8fb_0%,transparent_60%)]" />
  <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-12 md:py-16 grid md:grid-cols-2 gap-6 md:gap-10 items-center">
    {/* Image frame */}
    <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-sm">
      <div className="relative aspect-[16/10] md:aspect-[4/3]">
        <img
          src={heroBoiler}
          alt="Boiler, cylinder and ASHP installs and servicing"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/0 via-black/0 to-black/0" />
      </div>
    </div>

    {/* Text block */}
    <div>
      <p className="text-xs font-medium tracking-widest uppercase text-[#3bb3ef]">Heating & Hot Water</p>
      <h2 id="boilers-title" className="mt-2 text-2xl md:text-3xl font-semibold">
        Boilers, cylinders &amp; air source heat pumps
      </h2>
      <p className="mt-2 text-neutral-700">
        We size the system properly, explain the choices and keep everything tidy. We’ll also look after it once it’s in.
      </p>
      <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-neutral-700">
        <li>Fixed, itemised quotes</li>
        <li>Neat pipework and clean finishes</li>
        <li>Servicing and aftercare</li>
        <li>Paperwork handled properly</li>
      </ul>
      <div className="mt-6 flex flex-wrap gap-3">
        <a
          href="/contact"
          className="inline-flex items-center rounded-md bg-[#3bb3ef] text-white px-4 py-2.5 text-sm font-medium hover:brightness-110"
        >
          Book a survey
        </a>
        <a
          href="tel:+441522692592"
          className="inline-flex items-center rounded-md border border-neutral-300 px-4 py-2.5 text-sm font-medium hover:bg-neutral-50"
        >
          Call 01522 692592
        </a>
      </div>
    </div>
  </div>
</section>

{/* ───────────────── Bathrooms ───────────────── */}
<section
  id="bathrooms"
  aria-labelledby="bathrooms-title"
  className="relative scroll-mt-24 md:scroll-mt-28 border-t border-neutral-200 bg-white"
>
  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_40%_at_80%_0%,#f6f8fb_0%,transparent_60%)]" />
  <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-12 md:py-16 grid md:grid-cols-2 gap-6 md:gap-10 items-center">
    {/* Text first on desktop */}
    <div className="order-2 md:order-1">
      <p className="text-xs font-medium tracking-widest uppercase text-[#3bb3ef]">Bathrooms</p>
      <h2 id="bathrooms-title" className="mt-2 text-2xl md:text-3xl font-semibold">Bathrooms</h2>
      <p className="mt-2 text-neutral-700">
        One accountable team from first fix to final silicone. Clear dates. Clean handover.
      </p>
      <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-neutral-700">
        <li>Layout and specification help</li>
        <li>Coordinated trades</li>
        <li>Finishes you can live with</li>
        <li>Waste out, site protected</li>
      </ul>
      <div className="mt-6 flex flex-wrap gap-3">
        <a
          href="/contact"
          className="inline-flex items-center rounded-md bg-[#3bb3ef] text-white px-4 py-2.5 text-sm font-medium hover:brightness-110"
        >
          Start your bathroom
        </a>
        <a
          href="tel:+441522692592"
          className="inline-flex items-center rounded-md border border-neutral-300 px-4 py-2.5 text-sm font-medium hover:bg-neutral-50"
        >
          Call 01522 692592
        </a>
      </div>
    </div>

    {/* Image second on desktop */}
    <div className="order-1 md:order-2 rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-sm">
      <div className="relative aspect-[16/10] md:aspect-[4/3]">
        <img
          src={heroBathroom}
          alt="Bathroom installs and refurbs"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/0 to-black/0" />
      </div>
    </div>
  </div>
</section>

{/* ───────────────── First fix / Second fix ───────────────── */}
<section
  id="firstfix"
  aria-labelledby="firstfix-title"
  className="relative scroll-mt-24 md:scroll-mt-28 border-t border-neutral-200 bg-white"
>
  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_40%_at_20%_0%,#f6f8fb_0%,transparent_60%)]" />
  <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-12 md:py-16 grid md:grid-cols-2 gap-6 md:gap-10 items-center">
    {/* Image */}
    <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-sm">
      <div className="relative aspect-[16/10] md:aspect-[4/3]">
        <img
          src={heroFix}
          alt="First fix and second fix for new build and refurb"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/0 to-black/0" />
      </div>
    </div>

    {/* Text */}
    <div>
      <p className="text-xs font-medium tracking-widest uppercase text-[#3bb3ef]">New build & Refurb</p>
      <h2 id="firstfix-title" className="mt-2 text-2xl md:text-3xl font-semibold">First fix and second fix</h2>
      <p className="mt-2 text-neutral-700">
        New build or refurbishment. We work to the drawings, keep the site safe and hand it over clean.
      </p>
      <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-neutral-700">
        <li>Pipework set-out and pressure tests</li>
        <li>Sanitaryware, radiators and controls</li>
        <li>Snags closed before handover</li>
        <li>Clear sign-off</li>
      </ul>
      <div className="mt-6 flex flex-wrap gap-3">
        <a
          href="/contact"
          className="inline-flex items-center rounded-md bg-[#3bb3ef] text-white px-4 py-2.5 text-sm font-medium hover:brightness-110"
        >
          Check dates & pricing
        </a>
        <a
          href="tel:+441522692592"
          className="inline-flex items-center rounded-md border border-neutral-300 px-4 py-2.5 text-sm font-medium hover:bg-neutral-50"
        >
          Call 01522 692592
        </a>
      </div>
    </div>
  </div>
</section>


      {/* Gallery */}
      <section className="py-12 md:py-16 border-t border-neutral-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex items-end justify-between gap-3">
            <div>
              <h3 className="text-lg md:text-2xl font-semibold tracking-tight">Recent work</h3>
              <p className="mt-1 text-sm text-neutral-700">A few examples from installs and refurbs.</p>
            </div>
            <Link to="/contact" className="hidden sm:inline-flex items-center rounded-md bg-[#3bb3ef] text-white px-4 py-2.5 text-sm font-medium hover:brightness-110">
              Ask about availability
            </Link>
          </div>

          {/* Masonry */}
          <div className="mt-6 columns-2 sm:columns-3 lg:columns-4 gap-4 [column-fill:_balance]">
            {GALLERY.map((img, i) => (
              <button
                key={i}
                className="group mb-4 block w-full overflow-hidden rounded-xl border border-neutral-200 hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#3bb3ef]"
                onClick={() => setLightboxIndex(i)}
                aria-label={`Open image ${i + 1}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-auto block select-none [break-inside:_avoid]"
                  loading="lazy"
                  decoding="async"
                />
              </button>
            ))}
          </div>
        </div>

        {/* Lightbox with proper contain + swipe */}
        {lightboxIndex !== null && (
          <div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4"
            onClick={() => setLightboxIndex(null)}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="relative w-full max-w-6xl flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              <img
                src={GALLERY[lightboxIndex].src}
                alt={GALLERY[lightboxIndex].alt}
                className="block rounded-md shadow-lg"
                style={{
                  maxWidth: "95vw",
                  maxHeight: "85vh",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-white/90 text-xs sm:text-sm px-2 py-1 bg-black/30 rounded">
                {GALLERY[lightboxIndex].alt}
              </div>

              {/* Controls (visible on mobile too) */}
              <button
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-white/90 hover:bg-white text-neutral-900"
                onClick={() => setLightboxIndex((i) => (i === null ? i : (i - 1 + GALLERY.length) % GALLERY.length))}
                aria-label="Previous image"
              >
                ‹
              </button>
              <button
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-white/90 hover:bg-white text-neutral-900"
                onClick={() => setLightboxIndex((i) => (i === null ? i : (i + 1) % GALLERY.length))}
                aria-label="Next image"
              >
                ›
              </button>
              <button
                className="absolute top-2 right-2 sm:top-4 sm:right-4 h-9 w-9 rounded-full bg-white/90 hover:bg-white text-neutral-900"
                onClick={() => setLightboxIndex(null)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Final CTA */}
      <section className="py-12 md:py-14 bg-gradient-to-r from-[#3bb3ef] to-[#1f8fd3] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
          <div>
            <h3 className="text-lg md:text-xl font-semibold">Want to book a survey?</h3>
            <p className="opacity-90 text-[13px] md:text-sm">Tell us what you need and we’ll confirm dates and pricing.</p>
          </div>
          <div className="flex gap-2 md:gap-3">
            <a href="tel:+441522692592" className="inline-flex items-center rounded-md bg-white text-[#187fbf] px-4 md:px-5 py-2.5 text-[13px] md:text-sm font-medium hover:brightness-95">
              Call 01522 692592
            </a>
            <Link to="/contact" className="inline-flex items-center rounded-md border border-white/80 px-4 md:px-5 py-2.5 text-[13px] md:text-sm font-medium hover:bg-white/10">
              Request a quote
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
